import { motion } from "motion/react";
import { useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { Avatar } from "../components/Avatar";
import { Btn } from "../components/Button";
import { CodeTiles } from "../components/CodeTiles";
import { Confetti } from "../components/Confetti";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";
import { ArrowRight, Clock, Users } from "../components/Icon";
import { LoopVideo } from "../components/LoopVideo";
import { GAME_LIST, type GameMeta } from "../games/meta";
import { useI18n } from "../i18n";
import { asset, IS_DEMO } from "../lib/asset";
import { play } from "../lib/sfx";

const CODE_RE = /[^ABCDEFGHJKMNPQRSTUVWXYZ]/g;

export function Home() {
  return (
    <div className="min-h-dvh">
      <Header />
      <main>
        <Hero />
        <Games />
        <TryIt />
        <How />
        <Night />
        <JokerPromo />
      </main>
      <Footer />
    </div>
  );
}

function JoinBox() {
  const { d } = useI18n();
  const [code, setCode] = useState("");
  const [, nav] = useLocation();
  return (
    <form
      className="flex w-full min-w-0 max-w-md items-center gap-2 rounded-full bg-ground-3 p-2 sm:w-[320px]"
      onSubmit={(e) => {
        e.preventDefault();
        if (code.length >= 4) nav(`/j/${code}`);
      }}
    >
      <label className="sr-only" htmlFor="home-code">{d.join.code}</label>
      <input
        id="home-code"
        size={6}
        className="w-0 min-w-0 flex-1 bg-transparent px-4 text-xl font-black tracking-[0.3em] uppercase placeholder:tracking-normal placeholder:font-semibold placeholder:text-ink-3 focus:outline-none"
        placeholder={d.home.joinPlaceholder}
        value={code}
        maxLength={5}
        autoCapitalize="characters"
        autoComplete="off"
        inputMode="text"
        onChange={(e) => setCode(e.target.value.toUpperCase().replace(CODE_RE, ""))}
      />
      <Btn type="submit" variant="flame" size="sm" disabled={code.length < 4} icon={<ArrowRight size={19} />}>
        {d.home.joinGo}
      </Btn>
    </form>
  );
}

/** The closing "!" of the shout burns in flame colour. */
function HeroShout({ text }: { text: string }) {
  const bang = text.endsWith("!");
  return (
    <>
      {bang ? text.slice(0, -1) : text}
      {bang && <span className="text-flame">!</span>}
    </>
  );
}

function Hero() {
  const { d } = useI18n();
  return (
    <section className="shell pt-8 pb-16 md:pt-16 md:pb-24">
      {/* One shout, sized from its measured width so it fills the row exactly in every language. */}
      <div style={{ containerType: "inline-size" }}>
        <h1
          className="display whitespace-nowrap leading-[1.08]"
          style={{ fontSize: `min(200px, calc(100cqi / ${d.home.heroEm} * 0.985))` }}
        >
          <HeroShout text={d.home.heroTitle} />
        </h1>
      </div>
      <div className="mt-6 grid grid-cols-1 items-end gap-8 md:mt-8 md:grid-cols-[minmax(0,1fr)_auto]">
        <p className="max-w-xl text-xl text-ink-2 md:text-2xl">{d.home.heroSub}</p>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <Btn size="lg" onClick={() => document.getElementById("games")?.scrollIntoView({ behavior: "smooth" })}>
            {d.home.create}
          </Btn>
          <JoinBox />
        </div>
      </div>
      {IS_DEMO && <p className="mt-5 max-w-xl text-sm text-ink-3">{d.demo.note}</p>}
      <div className="relative mt-10 aspect-[16/9] w-full overflow-hidden rounded-[36px] bg-ground-2">
        <LoopVideo src="video/hero.mp4" poster="img/brand/hero.webp" className="absolute inset-0 h-full w-full object-cover" alt="" />
      </div>
    </section>
  );
}

function GameRow({ g, i }: { g: GameMeta; i: number }) {
  const { d } = useI18n();
  const t = d.games[g.id];
  const flip = i % 2 === 1;
  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="grid overflow-hidden rounded-[32px] md:grid-cols-2"
      style={{ background: `color-mix(in oklab, ${g.color} 16%, var(--color-ground-2))` }}
    >
      <div className={`relative aspect-[4/3] md:aspect-auto md:min-h-[400px] ${flip ? "md:order-2" : ""}`} style={{ background: g.deep }}>
        <LoopVideo src={g.video} poster={g.poster} className="absolute inset-0 h-full w-full object-cover" alt={t.name} />
      </div>
      <div className="flex flex-col justify-center gap-5 p-6 sm:p-10">
        <h3 className="display text-4xl sm:text-5xl" style={{ color: g.color }}>{t.name}</h3>
        <p className="text-2xl font-extrabold">{t.tagline}</p>
        <p className="text-lg text-ink-2">{t.short}</p>
        <p className="flex flex-wrap gap-x-5 gap-y-1 font-bold text-ink-2">
          <span className="inline-flex items-center gap-1.5"><Users size={18} /> {t.players}</span>
          {t.time !== "∞" && <span className="inline-flex items-center gap-1.5"><Clock size={18} /> {d.common.minutes.replace("{n}", t.time)}</span>}
        </p>
        <div>
          <Link
            href={g.href}
            className="btn btn-lg"
            style={{ "--b": g.color, "--s": g.deep, "--i": g.ink } as React.CSSProperties}
            onClick={() => play("pop")}
          >
            {d.common.play} <ArrowRight size={22} />
          </Link>
        </div>
      </div>
    </motion.article>
  );
}

function Games() {
  const { d } = useI18n();
  return (
    <section id="games" className="shell scroll-mt-20 py-10">
      <div className="mb-10 max-w-3xl">
        <h2 className="display text-[clamp(34px,5vw,60px)]">{d.home.gamesTitle}</h2>
        <p className="mt-4 text-xl text-ink-2">{d.home.gamesSub}</p>
      </div>
      <div className="space-y-6">
        {GAME_LIST.map((g, i) => (
          <GameRow key={g.id} g={g} i={i} />
        ))}
      </div>
    </section>
  );
}

function TryIt() {
  const { d } = useI18n();
  const [picked, setPicked] = useState<string | null>(null);
  const [round, setRound] = useState(0);
  const options = useMemo(() => {
    const all = [d.home.tryA, ...d.home.tryLies];
    return all.map((x, k) => ({ x, k: (k * 7 + round * 3) % 11 })).sort((a, b) => a.k - b.k).map((o) => o.x);
  }, [d, round]);
  const [before, after] = d.home.tryQ.split("____");
  const right = picked === d.home.tryA;
  return (
    <section className="shell py-24">
      <div className="grid gap-10 md:grid-cols-[1fr_1.1fr] md:items-center">
        <div>
          <h2 className="display text-[clamp(34px,5vw,60px)]">{d.home.tryTitle}</h2>
          <p className="mt-4 text-xl text-ink-2">{d.home.trySub}</p>
          <img src={asset("img/games/bluff.webp")} alt="" className="mt-8 hidden w-56 md:block" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
        </div>
        <div className="surface p-6 sm:p-8">
          <p className="text-2xl leading-snug font-extrabold sm:text-[28px]">
            {before}
            <span className="mx-1 inline-block min-w-[4ch] rounded-lg px-2 text-center" style={{ background: picked ? (right ? "var(--color-good)" : "var(--color-candy)") : "var(--color-ground-4)", color: picked ? "#160626" : "transparent" }}>
              {picked ? d.home.tryA : "____"}
            </span>
            {after}
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {options.map((o) => {
              const isTruth = o === d.home.tryA;
              const state = !picked ? "idle" : isTruth ? "truth" : o === picked ? "wrong" : "dim";
              return (
                <button
                  key={o}
                  className="tile px-5 py-4 text-left text-lg font-extrabold"
                  disabled={!!picked}
                  style={{
                    ["--b" as string]: state === "truth" ? "var(--color-good)" : state === "wrong" ? "var(--color-candy)" : "var(--color-ground-3)",
                    color: state === "truth" ? "#06281a" : undefined,
                    opacity: state === "dim" ? 0.5 : 1,
                  }}
                  onClick={() => {
                    setPicked(o);
                    play(o === d.home.tryA ? "fanfare" : "fooled");
                  }}
                >
                  {o}
                </button>
              );
            })}
          </div>
          {picked && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 space-y-3">
              <p className="text-xl font-black">{right ? d.home.tryRight : `${d.home.tryWrong} ${d.home.tryA}`}</p>
              <p className="text-ink-2">{d.home.tryNote}</p>
              <div className="flex flex-wrap gap-3 pt-2">
                <Link href="/play/bluff" className="btn" style={{ "--b": "var(--color-bluff)", "--s": "var(--color-bluff-deep)" } as React.CSSProperties}>
                  {d.games.bluff.name} <ArrowRight size={20} />
                </Link>
                <Btn variant="soft" onClick={() => { setPicked(null); setRound((r) => r + 1); }}>{d.home.tryAgain}</Btn>
              </div>
            </motion.div>
          )}
          <Confetti fire={right ? round + 1 : 0} />
        </div>
      </div>
    </section>
  );
}

function How() {
  const { d } = useI18n();
  const steps = [d.home.how1, d.home.how2, d.home.how3];
  return (
    <section className="bg-ground-2 py-24">
      <div className="shell grid gap-12 md:grid-cols-2 md:items-center">
        <div>
          <h2 className="display text-[clamp(34px,5vw,60px)]">{d.home.howTitle}</h2>
          <ol className="mt-8 space-y-6">
            {steps.map((s, i) => (
              <li key={i} className="flex gap-4 text-xl">
                <span className="mt-1 h-3 w-3 shrink-0 rounded-full" style={{ background: ["var(--color-candy)", "var(--color-flame)", "var(--color-spy)"][i] }} />
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>
        {/* A slice of the real lobby: the code, the QR card and the avatars arriving */}
        <div data-ui className="mx-auto w-full max-w-sm rounded-[34px] bg-ground p-6">
          <p className="text-center font-bold text-ink-3">{d.lobby.code}</p>
          <div className="mt-3 flex justify-center">
            <CodeTiles code="KHMQ" size={52} />
          </div>
          <div className="mt-6 grid grid-cols-4 gap-3">
            {[0, 8, 16, 14, 7, 3, 11, 17].map((a, i) => (
              <motion.div
                key={a}
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.25 + i * 0.12, type: "spring", stiffness: 500, damping: 18 }}
                className="flex justify-center"
              >
                <Avatar index={a} color={i} size={62} mood={i === 0 ? "happy" : "still"} />
              </motion.div>
            ))}
          </div>
          <div className="mt-6">
            <span className="btn btn-block" style={{ "--b": "var(--color-bluff)", "--s": "var(--color-bluff-deep)" } as React.CSSProperties}>{d.lobby.start}</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Night() {
  const { d } = useI18n();
  return (
    <section className="py-24" style={{ background: "#2b0716" }}>
      <div className="shell grid gap-10 md:grid-cols-[1.2fr_1fr] md:items-center">
        <div>
          <h2 className="display text-[clamp(34px,5vw,60px)] text-candy">{d.home.adultTitle}</h2>
          <p className="mt-5 max-w-2xl text-xl text-ink-2">{d.home.adultSub}</p>
        </div>
        <div className="flex justify-center gap-4">
          <Avatar index={6} size={130} mood="happy" />
          <Avatar index={14} size={130} mood="idle" />
        </div>
      </div>
    </section>
  );
}

function JokerPromo() {
  const { d } = useI18n();
  const g = GAME_LIST.find((x) => x.id === "joker")!;
  const sample = [
    ["ნინო", 450, 100, 50],
    ["გიო", 150, 300, 20],
    ["ანა", 100, 50, 650],
    ["ლუკა", 50, 250, 100],
  ] as const;
  return (
    <section className="shell py-24">
      <div className="grid gap-10 md:grid-cols-2 md:items-center">
        <div data-ui className="surface overflow-hidden p-5 font-bold">
          <div className="grid grid-cols-4 gap-2 text-center text-ink-3">
            {sample.map(([n]) => <span key={n}>{n}</span>)}
          </div>
          {[1, 2, 3].map((row) => (
            <div key={row} className="mt-2 grid grid-cols-4 gap-2 text-center">
              {sample.map((s) => (
                <span key={s[0]} className="tabular rounded-xl bg-ground-3 py-2 text-lg">{s[row]}</span>
              ))}
            </div>
          ))}
          <div className="mt-3 grid grid-cols-4 gap-2 text-center">
            {sample.map((s) => (
              <span key={s[0]} className="tabular rounded-xl py-2 text-xl font-black" style={{ background: g.color, color: g.ink }}>
                {s[1] + s[2] + s[3]}
              </span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="display text-[clamp(34px,5vw,60px)]" style={{ color: g.color }}>{d.games.joker.name}</h2>
          <p className="mt-4 text-xl text-ink-2">{d.games.joker.short}</p>
          <Link href="/joker" className="btn btn-lg mt-8" style={{ "--b": g.color, "--s": g.deep, "--i": g.ink } as React.CSSProperties}>
            {d.joker.newGame} <ArrowRight size={22} />
          </Link>
        </div>
      </div>
    </section>
  );
}
